/**
 * @bos/portal3 — THE DATA MODEL ROOM (`/app/runs/:id/plan/data-model`).
 *
 * The review that used to be the workbench's default bottom section, now a room with its
 * own address: the entity cards, the "what changed since the version you read" diff, the
 * approve door, the disagree door, the exact-edit composer and its ordered basket. Nothing
 * about the doors changed — only where they live, and that a link to them survives a
 * refresh and can be sent to someone.
 *
 * The DOUBLE GATE is unchanged and is the reason the door renders at all: the journal says
 * the run is waiting AND the disk (the approval state) says this model is what it is
 * waiting on. Either alone is a guess.
 */
import { useOwnerEdits, useRunDataModel } from '../hooks';
import { DataModelReview } from '../DataModelReview';
import { RoomQuestions } from '../RoomQuestions';
import { useRunRoom } from '../roomContext';

/** This room's slug — the routing key for the questions that name the `er` stage. */
const ROOM = 'plan/data-model';

export function DataModelRoom() {
  const { runId, ready, statusKey, view, questions, submitAnswers, intake } = useRunRoom();
  const {
    dataModel,
    diff,
    dmStatus,
    dmError,
    approve,
    requestRevision,
    previewEdit,
    submitEdit,
    previewBasket,
    submitBasket,
  } = useRunDataModel(runId, ready, statusKey);
  /* THE ORDERS ALREADY ON THE RECORD — the source of the picture's ordered chips. The room
     reads them itself (the room law: everything but the run row and the journal is a room's
     own fetch), and an unread or empty ledger simply means no chip is drawn. */
  const { ownerEdits } = useOwnerEdits(runId, ready, statusKey);

  const door =
    view.status === 'awaiting-owner' &&
    dataModel !== null &&
    (dataModel.approval.state === 'awaiting' || dataModel.approval.state === 'stale');

  /* WHAT THE MACHINE ASKED ABOUT THE MODEL — above the model, in every one of this room's
     states including the ones where there is no model yet. That is deliberate: a question
     minted while the ER stage was still deriving is exactly the case the subject strip's
     honest-absence fallback exists for, and hiding the question behind "nothing to review
     here yet" would strand it where it cannot be answered. */
  const asked = (
    <RoomQuestions
      room={ROOM}
      questions={questions}
      onSubmit={submitAnswers}
      intake={intake}
      artifactDerived={dmStatus === 'ready' && dataModel !== null && dataModel.model !== null}
      runClosed={view.status === 'completed'}
    />
  );

  if (dmStatus === 'loading' || dmStatus === 'idle') {
    return (
      <>
        {asked}
        <p className="page__note" aria-busy="true">
          Reading your data model…
        </p>
      </>
    );
  }

  if (dmStatus === 'error') {
    return (
      <>
        {asked}
        <p className="form__error" role="alert">
          {dmError ?? 'We couldn’t read this build’s data model.'}
        </p>
      </>
    );
  }

  /* A DATA MODEL THAT CANNOT BE READ BACK gets a section of its own rather than the
     silence of a component that renders only when `model !== null`. The run is usually
     still standing at the door when this happens, so the person needs to be told what the
     file is, that the door is still there, and what recovers it. */
  if (dataModel?.modelState === 'unintelligible') {
    return (
      <>
        {asked}
        <section className="dm" aria-label="Data model">
          <h2 className="dm__title">Data model</h2>
          <p className="dm__lede">
            The machine derived a data model for this build and wrote it to disk, and it
            cannot be read back — the file is damaged, or it was left half-written. This is a
            broken file, not an empty build: nothing about your brief has been lost.
          </p>
          <p className="dm__lede">
            {door
              ? 'The run is still stopped at this door. Asking for the data model to be derived again rebuilds it from your brief, and the run will stop here once more so you can read it.'
              : 'Asking for the data model to be derived again rebuilds it from your brief.'}
          </p>
        </section>
      </>
    );
  }

  if (dataModel === null || dataModel.model === null) {
    return (
      <>
        {asked}
        <section className="dm" aria-label="Data model">
          <h2 className="dm__title">Data model</h2>
          <p className="page__note">
            Nothing to review here yet — the run hasn’t produced a reviewable artifact. It
            appears here as soon as it exists.
          </p>
        </section>
      </>
    );
  }

  return (
    <>
      {asked}
      <DataModelReview
        payload={dataModel}
        diff={diff}
        door={door}
        onApprove={approve}
        onRequestRevision={requestRevision}
        onPreviewEdit={previewEdit}
        onEdit={submitEdit}
        onPreviewBasket={previewBasket}
        onApplyBasket={submitBasket}
        orders={ownerEdits?.rows}
      />
    </>
  );
}
